//@ts-check
import { Header } from '../components/header';
import { Browser } from '../utils/browsercheck';
import { slideDown, slideToggle, slideUp } from '../utils/effects';

export class DropdownMenu {
    /** The root element of the menu. */
    root;

    /** The burger menu */
    navButton;

    /** @type {{ element: HTMLElement, close: HTMLElement } | null} */
    overlay = null;

    /** The menu options */
    options;

    /** @type {Array<Submenu>} */
    submenus = [];

    /**
     * @param {import('../components/menu').MenuOptions} options
     */
    constructor(options) {
        this.options = options;
        this.root = this.#query(options.selector);
        this.navButton = this.#query(options.navButton);

        if (options.overlay) {
            this.overlay = {
                element: this.#query(options.overlay.element),
                close: this.#query(options.overlay.close),
            };
        }

        this.#getSubmenus();
        this.#registerEvents();

        if (this.inMobileMode()) {
            this.openTrail();
        }
    }

    inMobileMode() {
        return window.innerWidth < this.options.breakPoint;
    }

    toggleMenu() {
        slideToggle(this.root, 0.5, () => {
            Header.root.classList.toggle('menu-open');
        });

        this.navButton.classList.toggle('active');
    }

    openTrail() {
        this.submenus.forEach((submenu) => {
            if (submenu.state.isTrail && !submenu.state.isOpen) submenu.openLevel();
        });
    }

    toggleOverlay() {
        if (this.overlay?.element.classList.contains('open')) {
            this.overlay.element.classList.replace('open', 'close');
            setTimeout(() => this.overlay?.element.classList.remove('close'), 300);
        } else {
            this.overlay?.element.classList.add('open');
        }
    }

    hasOpenLevels() {
        return this.submenus.some((submenu) => submenu.state.isOpen);
    }

    /**
     * @param {number} level
     */
    closeOpenLevels(level = 1) {
        this.submenus.forEach((submenu) => {
            if (submenu.state.isOpen && submenu.level >= level) submenu.closeLevel();
        });
    }

    /**
     * @param {string} selector
     *
     * @returns {HTMLElement}
     */
    #query(selector) {
        const element = document.querySelector(selector);

        if (!(element instanceof HTMLElement)) {
            throw new Error(`Could not find element with selector ${selector}`);
        }

        return element;
    }

    #getSubmenus() {
        this.root.querySelectorAll('a.submenu, strong.submenu').forEach((link) => {
            this.submenus.push(new Submenu(link, this));
        });
    }

    #registerEvents() {
        if (this.overlay) {
            this.navButton.addEventListener('click', () => this.toggleOverlay());
            this.overlay.close.addEventListener('click', () => this.toggleOverlay());
        } else {
            this.navButton.addEventListener('click', () => this.toggleMenu());
        }

        if (Browser.isOnePage) {
            const links = this.root.querySelectorAll('a:not(.submenu)[href*="#"]');
            links.forEach((link) => link.addEventListener('click', () => this.#onLinkClick()));
        }

        window.addEventListener('click', (e) => this.#onClickOutside(e));
        window.addEventListener('resize', () => this.#onResize());
    }

    #onLinkClick() {
        // In case of one-page navigation, close the menu when a link is clicked
        if (this.inMobileMode()) this.toggleMenu();
    }

    #onResize() {
        this.inMobileMode() ? this.openTrail() : this.#resetMobileMenu();
    }

    /**
     * @param {MouseEvent} event
     */
    #onClickOutside(event) {
        if (this.inMobileMode() || !Browser.isTouchDevice) {
            return;
        }

        if (
            event.target instanceof HTMLElement &&
            !this.root.contains(event.target) &&
            !this.navButton.contains(event.target)
        ) {
            this.closeOpenLevels();
        }
    }

    #resetMobileMenu() {
        this.#resetOpenLevels();
        this.root.style.display = '';
        this.navButton.classList.remove('active');
        Header.root?.classList.remove('menu-open');
    }

    #resetOpenLevels() {
        this.submenus.forEach((submenu) => {
            if (submenu.state.isOpen) submenu.closeLevel(0);
        });
    }
}

class Submenu {
    /** The parent DropdownMenu instance. */
    parent;

    /** The link that opens the submenu (a) */
    link;

    /** The parent container of the link (li) */
    container;

    /** The submenu itself (ul) */
    menu;

    /** The duration of the slide animation */
    duration = 0.2;

    /** The level number of the submenu */
    level;

    state = {
        isOpen: false,
        isTrail: false,
    };

    /** @type {gsap.core.Tween|null} */
    animation = null;

    /**
     * @param {Element} link
     * @param {DropdownMenu} parent
     */
    constructor(link, parent) {
        if (!(link instanceof HTMLAnchorElement || link.tagName === 'STRONG')) {
            throw new Error('Invalid link element for the submenu');
        }

        const container = link.parentElement;
        const menu = link.nextElementSibling;

        if (!(container instanceof HTMLLIElement) || !(menu instanceof HTMLUListElement)) {
            throw new Error('Invalid parent elements for the submenu');
        }

        this.parent = parent;
        this.link = link;
        this.container = container;
        this.menu = menu;

        const match = this.menu.className.match(/level_(\d+)/);
        this.level = match ? parseInt(match[1]) : 0;

        if (this.link.classList.contains('trail')) {
            this.state.isTrail = true;
        }

        this.#registerEvents();
    }

    openLevel() {
        this.menu.classList.remove('show-left');

        if (!this.parent.inMobileMode() && this.level > 2) {
            const containerRect = this.container.getBoundingClientRect();
            const availableSpace = window.innerWidth - containerRect.right;

            if (availableSpace < 300) {
                this.menu.classList.add('show-left');
            }
        }

        this.animation = slideDown(this.menu, this.duration);
        this.state.isOpen = true;
        this.link.classList.add('open');
        Header.root.classList.add('level-open');
    }

    closeLevel(duration = this.duration) {
        this.animation = slideUp(this.menu, duration, () => {
            this.link.classList.remove('open');
        });

        this.state.isOpen = false;

        if (!this.parent.hasOpenLevels()) {
            Header.root.classList.remove('level-open');
        }
    }

    #registerEvents() {
        this.container.addEventListener('mouseenter', () => this.#onHover('enter'));
        this.container.addEventListener('mouseleave', () => this.#onHover('leave'));
        this.container.addEventListener('click', (e) => this.#onClick(e));
    }

    /**
     * @param {'enter' | 'leave'} direction
     */
    #onHover(direction) {
        if (this.parent.inMobileMode() || Browser.isTouchDevice) {
            return;
        }

        direction === 'enter' ? this.openLevel() : this.closeLevel();
    }

    /**
     * @param {MouseEvent} event
     */
    #onClick(event) {
        if (!this.parent.inMobileMode() && !Browser.isTouchDevice) {
            return;
        }

        event.stopPropagation();

        if (event.target instanceof HTMLAnchorElement && !event.target.classList.contains('submenu')) {
            return;
        }

        event.preventDefault();

        if (this.state.isOpen) {
            this.closeLevel();
        } else {
            this.parent.closeOpenLevels(this.level);
            this.openLevel();
        }
    }
}
